/**
 * Brain Explorer - Module Definitions
 * AUTO-SYNCED from system-prompt-builder.js v4.3.14
 * Last sync: 2026-01-26
 *
 * Run `npm run sync-brain` to update this file.
 * Human-written content (layers 1-7) is preserved during sync.
 */

// ============================================================================
// CONNECTION TYPES - What data flows between modules
// ============================================================================
export const CONNECTION_TYPES = {
  identity: {
    label: "Identity Flow",
    color: "#8B5CF6",
    description: "WHO you are - business persona"
  },
  rules: {
    label: "Rule Enforcement",
    color: "#EF4444",
    description: "Quality rules and constraints"
  },
  analysis: {
    label: "Analysis Data",
    color: "#10B981",
    description: "Review analysis results"
  },
  style: {
    label: "Style Guidance",
    color: "#EC4899",
    description: "Tone and formatting"
  },
  examples: {
    label: "Example Reference",
    color: "#6366F1",
    description: "Few-shot examples"
  },
  output: {
    label: "Output Building",
    color: "#3B82F6",
    description: "Final prompt sections"
  }
};

// ============================================================================
// DETAILED CONNECTIONS - Smart navigation between modules
// ============================================================================
export const MODULE_CONNECTIONS = {
  "core-identity": {
    outgoing: [
      {
        to: "anti-sycophancy",
        type: "identity",
        description: "It becomes you → It never grovels for you",
        dataFlow: "<identity> section (~200 tokens)"
      },
      {
        to: "anti-echo",
        type: "identity",
        description: "It becomes you → It doesn't parrot your reviews",
        dataFlow: "<identity> section (~200 tokens)"
      },
      {
        to: "tone-instructions",
        type: "identity",
        description: "It becomes you → It matches your voice",
        dataFlow: "<identity> section (~200 tokens)"
      },
      {
        to: "reasoning",
        type: "identity",
        description: "It becomes you → It thinks before writing",
        dataFlow: "<identity> section (~200 tokens)"
      }
    ],
    incoming: [
      {
        from: "anti-sycophancy",
        type: "rules",
        description: "It never grovels for you → It becomes you",
        dataFlow: "Sovereignty rules in <identity> section"
      },
      {
        from: "anti-echo",
        type: "analysis",
        description: "It doesn't parrot your reviews → It becomes you",
        dataFlow: "ANTI-ECHO rules in <voice_guidance>"
      },
      {
        from: "reasoning",
        type: "analysis",
        description: "It thinks before writing → It becomes you",
        dataFlow: "sentimentHint"
      },
      {
        from: "tone-instructions",
        type: "style",
        description: "It matches your voice → It becomes you",
        dataFlow: "emojiGuidance"
      }
    ],
    tokens: {
      input: [
        "businessName",
        "businessType",
        "businessContext",
        "customInstructions",
        "city"
      ],
      output: [
        "<identity> section (~200 tokens)"
      ],
      process: "Builds the WHO - transforms business data into owner persona"
    }
  },
  "anti-sycophancy": {
    outgoing: [
      {
        to: "core-identity",
        type: "rules",
        description: "It never grovels for you → It becomes you",
        dataFlow: "Sovereignty rules in <identity> section"
      },
      {
        to: "reasoning",
        type: "rules",
        description: "It never grovels for you → It thinks before writing",
        dataFlow: "Sovereignty rules in <identity> section"
      }
    ],
    incoming: [
      {
        from: "core-identity",
        type: "identity",
        description: "It becomes you → It never grovels for you",
        dataFlow: "<identity> section (~200 tokens)"
      },
      {
        from: "reasoning",
        type: "analysis",
        description: "It thinks before writing → It never grovels for you",
        dataFlow: "sentimentHint"
      }
    ],
    tokens: {
      input: [
        "reviewText (negative sentiment detection)"
      ],
      output: [
        "Sovereignty rules in <identity> section"
      ],
      process: "Enforces dignity - prevents excessive apologies"
    }
  },
  "anti-echo": {
    outgoing: [
      {
        to: "core-identity",
        type: "analysis",
        description: "It doesn't parrot your reviews → It becomes you",
        dataFlow: "ANTI-ECHO rules in <voice_guidance>"
      },
      {
        to: "avoid-patterns",
        type: "analysis",
        description: "It doesn't parrot your reviews → It avoids AI slop",
        dataFlow: "ANTI-ECHO rules in <voice_guidance>"
      }
    ],
    incoming: [
      {
        from: "core-identity",
        type: "identity",
        description: "It becomes you → It doesn't parrot your reviews",
        dataFlow: "<identity> section (~200 tokens)"
      },
      {
        from: "avoid-patterns",
        type: "analysis",
        description: "It avoids AI slop → It doesn't parrot your reviews",
        dataFlow: "ANTI-SLOP rules in <voice_guidance>"
      }
    ],
    tokens: {
      input: [
        "reviewText (for echo detection)"
      ],
      output: [
        "ANTI-ECHO rules in <voice_guidance>"
      ],
      process: "Prevents review parroting - detects echo patterns"
    }
  },
  reasoning: {
    outgoing: [
      {
        to: "core-identity",
        type: "analysis",
        description: "It thinks before writing → It becomes you",
        dataFlow: "sentimentHint"
      },
      {
        to: "anti-sycophancy",
        type: "analysis",
        description: "It thinks before writing → It never grovels for you",
        dataFlow: "sentimentHint"
      },
      {
        to: "smart-brackets",
        type: "analysis",
        description: "It thinks before writing → It gives you choices",
        dataFlow: "sentimentHint"
      },
      {
        to: "response-length",
        type: "analysis",
        description: "It thinks before writing → You control the length",
        dataFlow: "sentimentHint"
      }
    ],
    incoming: [
      {
        from: "core-identity",
        type: "identity",
        description: "It becomes you → It thinks before writing",
        dataFlow: "<identity> section (~200 tokens)"
      },
      {
        from: "anti-sycophancy",
        type: "rules",
        description: "It never grovels for you → It thinks before writing",
        dataFlow: "Sovereignty rules in <identity> section"
      },
      {
        from: "smart-brackets",
        type: "analysis",
        description: "It gives you choices → It thinks before writing",
        dataFlow: "bracketLevel"
      },
      {
        from: "response-length",
        type: "analysis",
        description: "You control the length → It thinks before writing",
        dataFlow: "maxTokens"
      }
    ],
    tokens: {
      input: [
        "reviewText",
        "businessType"
      ],
      output: [
        "sentimentHint",
        "ambiguityScore",
        "reviewEntities",
        "<response_approach> section"
      ],
      process: "Analyzes before generating - sentiment, sarcasm, ambiguity detection"
    }
  },
  "smart-brackets": {
    outgoing: [
      {
        to: "reasoning",
        type: "analysis",
        description: "It gives you choices → It thinks before writing",
        dataFlow: "bracketLevel"
      },
      {
        to: "language-sentiment",
        type: "analysis",
        description: "It gives you choices → It reads between the lines",
        dataFlow: "bracketLevel"
      }
    ],
    incoming: [
      {
        from: "reasoning",
        type: "analysis",
        description: "It thinks before writing → It gives you choices",
        dataFlow: "sentimentHint"
      },
      {
        from: "language-sentiment",
        type: "analysis",
        description: "It reads between the lines → It gives you choices",
        dataFlow: "languageHint (auto-detect)"
      },
      {
        from: "ambiguity-detection",
        type: "analysis",
        description: "It spots the gaps → It gives you choices",
        dataFlow: "ambiguityScore (0-100)"
      }
    ],
    tokens: {
      input: [
        "ambiguityScore",
        "sentimentHint",
        "customizationScore",
        "includeBrackets"
      ],
      output: [
        "bracketLevel",
        "<brackets> section (~150 tokens)"
      ],
      process: "Generates strategic options - not synonyms but real alternatives"
    }
  },
  "tone-instructions": {
    outgoing: [
      {
        to: "core-identity",
        type: "style",
        description: "It matches your voice → It becomes you",
        dataFlow: "emojiGuidance"
      },
      {
        to: "few-shot-examples",
        type: "style",
        description: "It matches your voice → It learns from examples",
        dataFlow: "emojiGuidance"
      },
      {
        to: "response-length",
        type: "style",
        description: "It matches your voice → You control the length",
        dataFlow: "emojiGuidance"
      }
    ],
    incoming: [
      {
        from: "core-identity",
        type: "identity",
        description: "It becomes you → It matches your voice",
        dataFlow: "<identity> section (~200 tokens)"
      },
      {
        from: "few-shot-examples",
        type: "examples",
        description: "It learns from examples → It matches your voice",
        dataFlow: "<examples> section (~200-400 tokens)"
      },
      {
        from: "response-length",
        type: "analysis",
        description: "You control the length → It matches your voice",
        dataFlow: "maxTokens"
      }
    ],
    tokens: {
      input: [
        "tone",
        "businessType",
        "includeEmojis"
      ],
      output: [
        "emojiGuidance",
        "<voice_guidance> section (~100 tokens)"
      ],
      process: "Adapts voice - professional/friendly/formal/apologetic"
    }
  },
  "few-shot-examples": {
    outgoing: [
      {
        to: "tone-instructions",
        type: "examples",
        description: "It learns from examples → It matches your voice",
        dataFlow: "<examples> section (~200-400 tokens)"
      },
      {
        to: "language-sentiment",
        type: "examples",
        description: "It learns from examples → It reads between the lines",
        dataFlow: "<examples> section (~200-400 tokens)"
      }
    ],
    incoming: [
      {
        from: "tone-instructions",
        type: "style",
        description: "It matches your voice → It learns from examples",
        dataFlow: "emojiGuidance"
      },
      {
        from: "language-sentiment",
        type: "analysis",
        description: "It reads between the lines → It learns from examples",
        dataFlow: "languageHint (auto-detect)"
      }
    ],
    tokens: {
      input: [
        "businessType",
        "sentimentHint",
        "tone"
      ],
      output: [
        "<examples> section (~200-400 tokens)"
      ],
      process: "Selects 2-3 examples from 147+ database entries"
    }
  },
  "avoid-patterns": {
    outgoing: [
      {
        to: "anti-echo",
        type: "analysis",
        description: "It avoids AI slop → It doesn't parrot your reviews",
        dataFlow: "ANTI-SLOP rules in <voice_guidance>"
      }
    ],
    incoming: [
      {
        from: "anti-echo",
        type: "analysis",
        description: "It doesn't parrot your reviews → It avoids AI slop",
        dataFlow: "ANTI-ECHO rules in <voice_guidance>"
      }
    ],
    tokens: {
      input: [
        "AI_SLOP_PHRASES",
        "AI_SLOP_WORDS"
      ],
      output: [
        "ANTI-SLOP rules in <voice_guidance>"
      ],
      process: "Blacklists 50+ corporate/AI phrases and words"
    }
  },
  "language-sentiment": {
    outgoing: [
      {
        to: "smart-brackets",
        type: "analysis",
        description: "It reads between the lines → It gives you choices",
        dataFlow: "languageHint (auto-detect)"
      },
      {
        to: "few-shot-examples",
        type: "analysis",
        description: "It reads between the lines → It learns from examples",
        dataFlow: "languageHint (auto-detect)"
      },
      {
        to: "ambiguity-detection",
        type: "analysis",
        description: "It reads between the lines → It spots the gaps",
        dataFlow: "languageHint (auto-detect)"
      }
    ],
    incoming: [
      {
        from: "smart-brackets",
        type: "analysis",
        description: "It gives you choices → It reads between the lines",
        dataFlow: "bracketLevel"
      },
      {
        from: "few-shot-examples",
        type: "examples",
        description: "It learns from examples → It reads between the lines",
        dataFlow: "<examples> section (~200-400 tokens)"
      },
      {
        from: "ambiguity-detection",
        type: "analysis",
        description: "It spots the gaps → It reads between the lines",
        dataFlow: "ambiguityScore (0-100)"
      }
    ],
    tokens: {
      input: [
        "reviewText"
      ],
      output: [
        "languageHint (auto-detect)",
        "sentimentHint",
        "sarcasm detection"
      ],
      process: "Reads between lines - sarcasm, conditional negativity, hedging"
    }
  },
  "response-length": {
    outgoing: [
      {
        to: "reasoning",
        type: "analysis",
        description: "You control the length → It thinks before writing",
        dataFlow: "maxTokens"
      },
      {
        to: "tone-instructions",
        type: "analysis",
        description: "You control the length → It matches your voice",
        dataFlow: "maxTokens"
      }
    ],
    incoming: [
      {
        from: "reasoning",
        type: "analysis",
        description: "It thinks before writing → You control the length",
        dataFlow: "sentimentHint"
      },
      {
        from: "tone-instructions",
        type: "style",
        description: "It matches your voice → You control the length",
        dataFlow: "emojiGuidance"
      }
    ],
    tokens: {
      input: [
        "responseLength (user setting)",
        "reviewText",
        "sentimentHint"
      ],
      output: [
        "maxTokens",
        "lengthInstruction in <output> section"
      ],
      process: "User selects: auto/short/medium/detailed/comprehensive. Auto uses AI analysis."
    }
  },
  "ambiguity-detection": {
    outgoing: [
      {
        to: "language-sentiment",
        type: "analysis",
        description: "It spots the gaps → It reads between the lines",
        dataFlow: "ambiguityScore (0-100)"
      },
      {
        to: "smart-brackets",
        type: "analysis",
        description: "It spots the gaps → It gives you choices",
        dataFlow: "ambiguityScore (0-100)"
      }
    ],
    incoming: [
      {
        from: "language-sentiment",
        type: "analysis",
        description: "It reads between the lines → It spots the gaps",
        dataFlow: "languageHint (auto-detect)"
      }
    ],
    tokens: {
      input: [
        "reviewText",
        "businessType",
        "AMBIGUITY_PATTERNS"
      ],
      output: [
        "ambiguityScore (0-100)",
        "suggestedBrackets[]"
      ],
      process: "Industry-specific vague reference detection"
    }
  }
};

// ============================================================================
// TOKEN FLOW SUMMARY - Overall system token usage
// ============================================================================
export const TOKEN_FLOW_SUMMARY = {
  systemMessage: {
    total: "800-1200 tokens",
    breakdown: {
      identity: "~200 tokens",
      voice_guidance: "~100 tokens",
      response_approach: "~80 tokens",
      brackets: "~150 tokens (if enabled)",
      examples: "~200-400 tokens",
      output_format: "~70 tokens"
    }
  },
  userMessage: {
    total: "50-200 tokens",
    breakdown: {
      review: "variable",
      instructions: "~30 tokens"
    }
  },
  response: {
    total: "120-650 tokens",
    userSelectable: true,
    options: [
      "auto",
      "short",
      "medium",
      "detailed",
      "comprehensive"
    ],
    breakdown: {
      auto: "AI decides based on review complexity (default)",
      short: "50-120 tokens (quick acknowledgment)",
      medium: "80-200 tokens (standard response)",
      detailed: "150-350 tokens (thorough reply)",
      comprehensive: "250-500 tokens (full context)",
      bracketMultiplier: "1.3x if brackets enabled"
    }
  }
};

// ============================================================================
// CALCULATION DETAILS - How everything is computed
// ============================================================================
export const CALCULATION_DETAILS = {
  contextScore: {
    function: "calculateContextScore(context)",
    formula: "businessName(25) + businessContext(25) + businessType(25) + customInstructions(25) = 0-100",
    inputs: {
      businessName: {
        points: 25,
        condition: "exists AND !== \"Your Business\" AND !== \"generic\""
      },
      businessContext: {
        points: 25,
        condition: "exists AND length > 20 chars"
      },
      businessType: {
        points: 25,
        condition: "exists AND !== \"generic\" AND !== \"business\""
      },
      customInstructions: {
        points: 25,
        condition: "exists AND length > 10 chars"
      }
    },
    outputs: {
      score: "0-100 (drives bracket density)"
    },
    modes: {
      builderMode: "0-24 = ultra_aggressive brackets (user assembles)",
      assistedMode: "25-49 = aggressive brackets (50/50 mix)",
      contextMode: "50+ = moderate brackets (AI writes more)"
    }
  },
  bracketLevel: {
    function: "calculateSmartBracketLevel(ambiguityAnalysis, customizationScore, reviewEntities)",
    formula: "IF customizationScore < 25 → ultra_aggressive ELSE combinedScore decides",
    inputs: {
      ambiguityAnalysis: {
        type: "object",
        contains: [
          "score (0-100)",
          "bracketCount"
        ]
      },
      customizationScore: {
        type: "number",
        range: "0-100"
      },
      reviewEntities: {
        type: "object",
        contains: [
          "dish[]",
          "staff[]",
          "time[]",
          "price[]"
        ]
      }
    },
    scoring: {
      ambiguityFactor: "ambiguityScore (0-100)",
      customizationPenalty: "customizationScore (higher = fewer brackets)",
      entityBonus: "entityCount >= 2 → -15 (specific review needs fewer brackets)",
      combinedFormula: "ambiguityFactor - customizationPenalty + entityAdjustment"
    },
    outputs: {
      ultra_aggressive: "score >= 60 OR customizationScore < 25 → 8-10 brackets",
      aggressive: "score 40-59 → 5-7 brackets",
      moderate: "score 20-39 → 3-4 brackets",
      conservative: "score < 20 → 1-2 brackets"
    }
  },
  responseLength: {
    function: "calculateResponseLength(reviewText)",
    formula: "CRITICAL patterns → comprehensive, SEVERE → detailed, else length-based",
    inputs: {
      reviewText: {
        type: "string",
        purpose: "analyze for patterns and length"
      }
    },
    patterns: {
      critical: [
        "lawyer",
        "lawsuit",
        "health department",
        "food poisoning",
        "anwalt",
        "gesundheitsamt"
      ],
      severe: [
        "worst",
        "terrible",
        "disgusting",
        "never again",
        "nie wieder",
        "katastroph"
      ]
    },
    pointCounting: {
      sentenceBreaks: "count [.!?] followed by capital letter",
      numberedItems: "count 1. 2. 3. or a) b) c)",
      bulletItems: "count lines starting with - • *"
    },
    outputs: {
      comprehensive: "CRITICAL patterns detected → 250-500 tokens",
      detailed: "SEVERE patterns OR 4+ points → 150-350 tokens",
      medium: "2-3 points OR length > 200 chars → 80-200 tokens",
      short: "< 2 points AND length < 100 chars → 50-120 tokens"
    }
  },
  maxTokens: {
    function: "calculateMaxTokens(responseLength, includeBrackets)",
    formula: "TOKEN_LIMITS[responseLength] × (includeBrackets ? 1.3 : 1)",
    inputs: {
      responseLength: {
        type: "string",
        values: [
          "short",
          "medium",
          "detailed",
          "comprehensive"
        ]
      },
      includeBrackets: {
        type: "boolean",
        effect: "1.3x multiplier if true"
      }
    },
    tokenLimits: {
      short: {
        min: 50,
        target: 80,
        max: 120
      },
      medium: {
        min: 80,
        target: 140,
        max: 200
      },
      detailed: {
        min: 150,
        target: 250,
        max: 350
      },
      comprehensive: {
        min: 250,
        target: 400,
        max: 500
      }
    },
    bracketMultiplier: 1.3,
    outputs: {
      maxTokens: "limits.max × multiplier",
      minTokens: "limits.min × multiplier",
      targetTokens: "limits.target × multiplier"
    }
  },
  sentimentHint: {
    function: "getSentimentHint(reviewText, reviewRating)",
    formula: "text patterns → confidence score → sentiment OR rating fallback",
    inputs: {
      reviewText: {
        type: "string",
        purpose: "pattern matching"
      },
      reviewRating: {
        type: "number",
        range: "1-5",
        optional: true
      }
    },
    patterns: {
      severe_negative: [
        "worst",
        "terrible",
        "never again",
        "lawyer",
        "disgusting"
      ],
      negative: [
        "disappointed",
        "bad",
        "cold",
        "rude",
        "slow"
      ],
      mixed: [
        "but",
        "however",
        "although",
        "except"
      ],
      positive: [
        "good",
        "great",
        "nice",
        "enjoyed"
      ],
      enthusiastic: [
        "amazing",
        "incredible",
        "best ever",
        "fantastic"
      ],
      sarcastic: [
        "\"great\" + something bad",
        "love when [negative]"
      ]
    },
    confidenceThresholds: {
      high: "confidence >= 0.7 → use detected sentiment",
      medium: "confidence >= 0.4 → use detected sentiment",
      low: "confidence < 0.4 → fall back to rating"
    },
    ratingFallback: {
      "3": "mixed",
      "4": "positive",
      "5": "enthusiastic",
      "1-2": "negative"
    }
  },
  includeEmojis: {
    function: "shouldIncludeEmojis(tone, sentiment, userOverride)",
    formula: "IF userOverride !== null → userOverride ELSE toneDefault OR sentimentDefault",
    inputs: {
      tone: {
        type: "string",
        values: [
          "professional",
          "friendly",
          "formal",
          "apologetic"
        ]
      },
      sentiment: {
        type: "string",
        values: [
          "severe_negative",
          "negative",
          "mixed",
          "positive",
          "enthusiastic"
        ]
      },
      userOverride: {
        type: "boolean|null",
        effect: "overrides all if not null"
      }
    },
    defaults: {
      byTone: {
        friendly: "true (3-5 emojis)",
        professional: "true (1-2 emojis)",
        formal: "false (0-1 emoji)",
        apologetic: "false (0-1 emoji)"
      },
      bySentiment: {
        severe_negative: "false (never)",
        negative: "false (rarely)",
        mixed: "conditional",
        positive: "true",
        enthusiastic: "true"
      }
    }
  },
  ambiguityAnalysis: {
    function: "analyzeReviewAmbiguity(reviewText, businessType)",
    formula: "count vague references × 10 + no specifics × 20 = score (0-100)",
    inputs: {
      reviewText: {
        type: "string",
        purpose: "pattern matching"
      },
      businessType: {
        type: "string",
        purpose: "select industry patterns"
      }
    },
    industryPatterns: {
      restaurant: [
        "the food",
        "the dish",
        "the waiter",
        "the service"
      ],
      hotel: [
        "the room",
        "the staff",
        "the pool",
        "the breakfast"
      ],
      dental: [
        "the procedure",
        "the dentist",
        "the treatment"
      ],
      salon: [
        "the stylist",
        "the cut",
        "the color"
      ]
    },
    scoring: {
      vagueReference: "+10 per vague phrase detected",
      noSpecificNames: "+20 if no proper nouns",
      noSpecificDishes: "+15 if no concrete items mentioned",
      shortReview: "+10 if < 50 chars"
    },
    outputs: {
      score: "0-100 (higher = more ambiguous)",
      suggestedBrackets: "array of bracket types to generate",
      details: "object with detected vague elements"
    }
  },
  entityExtraction: {
    function: "extractReviewEntities(reviewText, businessType)",
    formula: "regex patterns per industry → extracted arrays",
    inputs: {
      reviewText: {
        type: "string",
        purpose: "entity extraction"
      },
      businessType: {
        type: "string",
        purpose: "select entity patterns"
      }
    },
    entityTypes: {
      restaurant: {
        dish: [
          "pizza",
          "pasta",
          "steak",
          "salad",
          "soup",
          "+ 50 more"
        ],
        drink: [
          "wine",
          "beer",
          "coffee",
          "+ 30 more"
        ],
        staff: [
          "waiter",
          "chef",
          "manager",
          "+ names"
        ]
      },
      hotel: {
        room_type: [
          "suite",
          "deluxe",
          "standard",
          "penthouse"
        ],
        amenity: [
          "pool",
          "spa",
          "gym",
          "breakfast"
        ],
        staff: [
          "receptionist",
          "concierge",
          "housekeeper"
        ]
      },
      dental: {
        procedure: [
          "cleaning",
          "filling",
          "root canal",
          "extraction"
        ],
        staff: [
          "dentist",
          "hygienist",
          "Dr. + name"
        ]
      }
    },
    outputs: {
      entities: "object with arrays per type",
      _meta: {
        extractionTime: "ms",
        patternMatches: "count"
      }
    }
  }
};

// ============================================================================
// FUNCTION CALL GRAPH - Which functions trigger which
// ============================================================================
export const FUNCTION_CALL_GRAPH = {
  entryPoint: "buildSovereignPrompt(config)",
  callSequence: [
    {
      step: 1,
      function: "buildSovereignPrompt",
      calls: [
        "getSentimentHint(reviewText, reviewRating)",
        "analyzeReviewAmbiguity(reviewText, businessType)",
        "extractReviewEntities(reviewText, businessType)",
        "calculateContextScore(context)"
      ],
      purpose: "Initial analysis phase"
    },
    {
      step: 2,
      function: "buildSovereignPrompt",
      calls: [
        "calculateSmartBracketLevel(ambiguityAnalysis, customizationScore, reviewEntities)",
        "calculateResponseLength(reviewText)",
        "shouldIncludeEmojis(tone, sentiment, userOverride)"
      ],
      purpose: "Calculate smart defaults"
    },
    {
      step: 3,
      function: "buildSovereignPrompt",
      calls: [
        "calculateMaxTokens(effectiveResponseLength, effectiveIncludeBrackets)",
        "getIndustryContext(businessType)",
        "buildBracketSection(options)",
        "getFewShotExamples(config)"
      ],
      purpose: "Build prompt sections"
    },
    {
      step: 4,
      function: "buildSovereignPrompt",
      returns: "{ systemMessage, userMessage, maxTokens, metadata }",
      purpose: "Assemble final output"
    }
  ],
  helperFunctions: {
    selectFewShotExamples: {
      calledBy: "getFewShotExamples",
      purpose: "Select 2-3 examples from 147+ database"
    },
    isPlaceholderBusinessName: {
      calledBy: "buildSovereignPrompt",
      purpose: "Check if business name is a placeholder"
    },
    calculateAutoTone: {
      calledBy: "calculateSmartDefaults (demo/email mode only)",
      purpose: "Auto-select tone based on sentiment"
    }
  }
};

// ============================================================================
// DATA FLOW STAGES - Input → Output at each stage
// ============================================================================
export const DATA_FLOW_STAGES = [
  {
    stage: "INPUT",
    description: "Raw user input and settings",
    data: {
      reviewText: "string - the review to respond to",
      businessName: "string - user setting or \"Your Business\"",
      businessType: "string - restaurant/hotel/dental/etc",
      businessContext: "string - user description of their business",
      customInstructions: "string - \"always mention X\"",
      tone: "string - professional/friendly/formal/apologetic",
      includeBrackets: "boolean - user preference",
      includeEmojis: "boolean|null - user preference or auto",
      responseLength: "string - auto/short/medium/detailed/comprehensive"
    }
  },
  {
    stage: "ANALYSIS",
    description: "Analyze review and context",
    transformations: {
      "reviewText → sentimentHint": "Pattern matching for negative/positive/sarcastic",
      "reviewText → ambiguityScore": "Count vague references (0-100)",
      "reviewText → reviewEntities": "Extract dishes, staff, times mentioned",
      "context fields → customizationScore": "4 fields × 25 points each = 0-100"
    }
  },
  {
    stage: "CALCULATION",
    description: "Compute dynamic settings",
    transformations: {
      "customizationScore → bracketLevel": "<25=ultra_aggressive, 25-49=aggressive, 50+=moderate",
      "ambiguityScore + entities → bracketLevel": "Combined with customization for final level",
      "reviewText patterns → responseLength": "CRITICAL→comprehensive, SEVERE→detailed",
      "responseLength + brackets → maxTokens": "TOKEN_LIMITS × 1.3 if brackets",
      "tone + sentiment → includeEmojis": "Unless user override"
    }
  },
  {
    stage: "PROMPT BUILDING",
    description: "Assemble the system prompt",
    sections: {
      "<identity>": "businessName + businessType + city → owner persona",
      "<business_context>": "businessContext → AI understands your business",
      "<response_style>": "customInstructions → always follow these",
      "<response_approach>": "sentimentHint → match review energy",
      "<this_review>": "reviewerName + sentimentHint → context for LLM",
      "<voice_guidance>": "tone + anti-echo + anti-slop → sound human",
      "<brackets>": "bracketLevel + sentiment → strategic choices",
      "<examples>": "businessType + sentiment → 2-3 few-shot examples",
      "<output>": "responseLength + tone + emojis → format rules"
    }
  },
  {
    stage: "OUTPUT",
    description: "Final result sent to LLM",
    data: {
      systemMessage: "string - 800-1200 tokens of instructions",
      userMessage: "string - 50-200 tokens with review",
      maxTokens: "number - calculated limit for response",
      metadata: "object - all settings for debugging/logging"
    }
  }
];

// ============================================================================
// VARIABLE DEPENDENCIES - What feeds into what
// ============================================================================
export const VARIABLE_DEPENDENCIES = {
  primaryInputs: [
    "reviewText",
    "businessName",
    "businessType",
    "businessContext",
    "customInstructions",
    "tone",
    "includeBrackets",
    "includeEmojis",
    "responseLength",
    "reviewRating",
    "reviewerName",
    "city"
  ],
  intermediates: {
    sentimentHint: {
      dependsOn: [
        "reviewText",
        "reviewRating"
      ],
      feedsInto: [
        "bracketSection",
        "responseApproach",
        "fewShotExamples",
        "emojiDecision"
      ]
    },
    ambiguityAnalysis: {
      dependsOn: [
        "reviewText",
        "businessType"
      ],
      feedsInto: [
        "bracketLevel",
        "suggestedBrackets"
      ]
    },
    reviewEntities: {
      dependsOn: [
        "reviewText",
        "businessType"
      ],
      feedsInto: [
        "bracketLevel",
        "specificityCheck"
      ]
    },
    customizationScore: {
      dependsOn: [
        "businessName",
        "businessContext",
        "businessType",
        "customInstructions"
      ],
      feedsInto: [
        "bracketLevel",
        "builderMode"
      ]
    },
    effectiveResponseLength: {
      dependsOn: [
        "responseLength (user)",
        "reviewText",
        "mode"
      ],
      feedsInto: [
        "maxTokens",
        "lengthInstruction"
      ]
    },
    effectiveBracketLevel: {
      dependsOn: [
        "ambiguityAnalysis",
        "customizationScore",
        "reviewEntities"
      ],
      feedsInto: [
        "bracketSection"
      ]
    },
    effectiveIncludeEmojis: {
      dependsOn: [
        "includeEmojis (user)",
        "tone",
        "sentimentHint"
      ],
      feedsInto: [
        "toneConfig",
        "outputFormat"
      ]
    }
  },
  finalOutputs: {
    systemMessage: {
      assembledFrom: [
        "identity section (businessName, businessType, city)",
        "business_context section (businessContext)",
        "response_style section (customInstructions)",
        "response_approach section (sentimentHint)",
        "this_review section (reviewerName, sentimentHint)",
        "voice_guidance section (tone, anti-echo rules)",
        "bracket section (bracketLevel, sentiment)",
        "examples section (businessType, sentimentHint)",
        "output section (responseLength, tone, emojis)"
      ]
    },
    userMessage: {
      assembledFrom: [
        "reviewerName",
        "reviewText",
        "oneTimeInstructions",
        "bracketReminder"
      ]
    },
    maxTokens: {
      calculatedFrom: [
        "effectiveResponseLength",
        "effectiveIncludeBrackets"
      ]
    }
  }
};

// ============================================================================
// TONE DEFINITIONS - How the AI adjusts its voice
// ============================================================================
export const TONE_DEFINITIONS = {
  professional: {
    name: 'Professional',
    icon: '💼',
    color: '#3B82F6',
    description: 'Professional and courteous - polished but warm',
    emojiGuidance: {
      enabled: 'You MAY include 1-2 relevant emojis if appropriate.',
      disabled: 'No emojis.'
    },
    bestFor: ['B2B services', 'Law firms', 'Medical practices', 'Corporate clients'],
    example: {
      review: 'The consultation was informative but felt rushed.',
      response: 'Thank you for sharing your experience. I understand time is precious, and I want to ensure every client feels fully heard. Let me offer you a follow-up call at no charge to address anything we missed. - Dr. Schmidt'
    }
  },
  friendly: {
    name: 'Friendly',
    icon: '😊',
    color: '#10B981',
    description: 'Super enthusiastic and fun! Like your best friend who LOVES what they do.',
    emojiGuidance: {
      enabled: 'USE 3-5 EMOJIS generously! Be expressive! 🎉',
      disabled: 'No emojis.'
    },
    bestFor: ['Restaurants', 'Cafes', 'Retail', 'Entertainment', 'Fitness studios'],
    example: {
      review: 'Pizza was great but the wait was long.',
      response: 'Hey! 🍕 So glad the pizza hit the spot! Saturday nights get wild here - next time call ahead and I will save you a table! 🙌 - Luigi'
    }
  },
  formal: {
    name: 'Formal',
    icon: '🎩',
    color: '#8B5CF6',
    description: 'Refined and elegant - for premium, upscale establishments.',
    emojiGuidance: {
      enabled: 'You MAY include 1 subtle emoji if appropriate.',
      disabled: 'No emojis.'
    },
    bestFor: ['Fine dining', 'Luxury hotels', 'High-end spas', 'Premium services'],
    example: {
      review: 'Excellent ambiance but the sommelier was dismissive.',
      response: 'We appreciate your discerning feedback. The experience you describe falls short of our standards. I have personally addressed this with our sommelier team. Please accept our invitation for a complimentary wine tasting. - The Grand Hotel'
    }
  },
  apologetic: {
    name: 'Apologetic',
    icon: '🙏',
    color: '#F59E0B',
    description: 'Genuinely sorry and solution-focused. Take full ownership.',
    emojiGuidance: {
      enabled: 'You MAY include 1 empathetic emoji.',
      disabled: 'No emojis.'
    },
    bestFor: ['Service recovery', 'Negative reviews', 'Complaints', 'When things went wrong'],
    example: {
      review: 'Cold food and rude waiter. Never coming back.',
      response: 'I am truly sorry. Cold food and poor service is not what we stand for. I would like to make this right - please reach out directly and your next meal is on the house. This matters to me. - Marco, Owner'
    }
  }
};

// ============================================================================
// INDUSTRY CONTEXTS - Pre-filled context by business type
// ============================================================================
export const INDUSTRY_CONTEXTS = {
  restaurant: {
    name: 'Restaurant',
    icon: '🍽️',
    color: '#EF4444',
    context: 'Signature dishes, peak hours, service team strengths, atmosphere, seasonal specials',
    voice: 'Warmth of someone who genuinely loves feeding people. You know your regulars by name.',
    details: ['food quality', 'service speed', 'ambiance', 'menu items', 'chef specialties'],
    examples: ['dishes', 'drinks', 'dining experience', 'reservations'],
    keywords: ['meal', 'food', 'dish', 'chef', 'waiter', 'table', 'menu']
  },
  hotel: {
    name: 'Hotel',
    icon: '🏨',
    color: '#3B82F6',
    context: 'Room types, check-in processes, amenities, guest expectations, local tips',
    voice: 'Professional hospitality. You take pride in making guests feel at home.',
    details: ['room quality', 'cleanliness', 'amenities', 'staff service', 'check-in experience'],
    examples: ['room types', 'facilities', 'breakfast', 'location convenience'],
    keywords: ['room', 'bed', 'check-in', 'stay', 'night', 'breakfast']
  },
  dental: {
    name: 'Dental Practice',
    icon: '🦷',
    color: '#06B6D4',
    context: 'Treatment procedures, patient anxiety, wait times, modern technology, pain management',
    voice: 'Calm and reassuring. You understand dental anxiety and take it seriously.',
    details: ['treatment quality', 'staff friendliness', 'wait times', 'pain management', 'explanations'],
    examples: ['procedures', 'hygiene', 'appointment scheduling', 'patient comfort'],
    keywords: ['tooth', 'teeth', 'pain', 'cleaning', 'dentist', 'appointment']
  },
  medical: {
    name: 'Medical Practice',
    icon: '⚕️',
    color: '#10B981',
    context: 'Treatment quality, diagnostic accuracy, wait times, patient communication, aftercare',
    voice: 'Compassionate professional. You understand health concerns are personal.',
    details: ['care quality', 'doctor attentiveness', 'wait times', 'staff professionalism'],
    examples: ['consultations', 'treatments', 'follow-up care', 'office environment'],
    keywords: ['doctor', 'treatment', 'diagnosis', 'health', 'appointment', 'wait']
  },
  fitness: {
    name: 'Fitness Studio',
    icon: '💪',
    color: '#F97316',
    context: 'Equipment, trainer qualifications, class offerings, cleanliness, member community',
    voice: 'Energetic and motivating. You care about members reaching their goals.',
    details: ['equipment quality', 'cleanliness', 'staff helpfulness', 'class variety'],
    examples: ['workouts', 'trainers', 'facilities', 'membership value'],
    keywords: ['workout', 'trainer', 'class', 'equipment', 'gym', 'exercise']
  },
  salon: {
    name: 'Hair Salon',
    icon: '💇',
    color: '#EC4899',
    context: 'Service quality, stylist expertise, consultations, atmosphere, products',
    voice: 'Creative and attentive. You want everyone to leave feeling confident.',
    details: ['service quality', 'stylist skills', 'ambiance', 'pricing', 'results'],
    examples: ['haircuts', 'coloring', 'treatments', 'styling', 'appointments'],
    keywords: ['hair', 'cut', 'color', 'stylist', 'appointment', 'style']
  },
  automotive: {
    name: 'Auto Shop',
    icon: '🔧',
    color: '#6366F1',
    context: 'Repair quality, price transparency, turnaround times, parts quality, diagnostics',
    voice: 'Straight-talking expert. You explain car issues in plain language.',
    details: ['repair quality', 'pricing transparency', 'turnaround time', 'communication'],
    examples: ['repairs', 'maintenance', 'diagnostics', 'customer service'],
    keywords: ['car', 'repair', 'mechanic', 'fix', 'service', 'oil']
  },
  retail: {
    name: 'Retail Store',
    icon: '🛍️',
    color: '#8B5CF6',
    context: 'Product quality, customer service, pricing, store atmosphere, availability',
    voice: 'Helpful and knowledgeable. You know your products inside out.',
    details: ['product quality', 'service', 'pricing', 'atmosphere', 'availability'],
    examples: ['products', 'advice', 'shopping experience', 'returns'],
    keywords: ['product', 'buy', 'purchase', 'store', 'shop', 'price']
  },
  ecommerce: {
    name: 'Online Shop',
    icon: '📦',
    color: '#14B8A6',
    context: 'Product quality, shipping speed, packaging, customer service, return process',
    voice: 'Customer-focused and solution-oriented. Fast, reliable, fair.',
    details: ['product quality', 'shipping speed', 'packaging', 'customer support'],
    examples: ['orders', 'returns', 'product descriptions', 'delivery experience'],
    keywords: ['order', 'shipping', 'delivery', 'return', 'package', 'product']
  },
  veterinarian: {
    name: 'Veterinary Practice',
    icon: '🐾',
    color: '#84CC16',
    context: 'Animal care, anxious pet owners, emergency protocols, follow-up care, species expertise',
    voice: 'Compassionate and reassuring. You love animals and understand the pet-owner bond.',
    details: ['care quality', 'staff compassion', 'wait times', 'treatment explanations', 'cleanliness'],
    examples: ['checkups', 'vaccinations', 'surgeries', 'emergency care', 'pet boarding'],
    keywords: ['pet', 'dog', 'cat', 'animal', 'vet', 'treatment']
  },
  contractor: {
    name: 'Contractor',
    icon: '🔨',
    color: '#A855F7',
    context: 'Project timelines, material quality, building codes, communication, unexpected issues',
    voice: 'Straightforward and reliable. You take pride in quality work that lasts.',
    details: ['work quality', 'timeline adherence', 'communication', 'cleanliness', 'pricing transparency'],
    examples: ['renovations', 'repairs', 'installations', 'quotes', 'project management'],
    keywords: ['work', 'project', 'renovation', 'build', 'install', 'repair']
  },
  legal: {
    name: 'Law Firm',
    icon: '⚖️',
    color: '#1E40AF',
    context: 'Case complexity, legal processes, fee structures, confidentiality, outcome expectations',
    voice: 'Professional and trustworthy. You explain complex legal matters clearly.',
    details: ['expertise', 'communication', 'responsiveness', 'case outcomes', 'fee transparency'],
    examples: ['consultations', 'case handling', 'court representation', 'document preparation'],
    keywords: ['lawyer', 'case', 'legal', 'court', 'attorney', 'consultation']
  },
  realestate: {
    name: 'Real Estate',
    icon: '🏠',
    color: '#059669',
    context: 'Market conditions, property search, negotiation, first-time buyer guidance, investments',
    voice: 'Knowledgeable and patient. You guide clients through major life decisions.',
    details: ['market knowledge', 'responsiveness', 'negotiation skills', 'communication', 'closing support'],
    examples: ['property search', 'viewings', 'negotiations', 'paperwork', 'market advice'],
    keywords: ['house', 'property', 'buy', 'sell', 'agent', 'viewing']
  },
  daycare: {
    name: 'Daycare',
    icon: '👶',
    color: '#F472B6',
    context: 'Child safety, age-appropriate activities, parent communication, meals, staff qualifications',
    voice: 'Warm and nurturing. You treat every child as if they were your own.',
    details: ['safety', 'staff attentiveness', 'cleanliness', 'activities', 'communication with parents'],
    examples: ['daily activities', 'meals', 'nap time', 'learning programs', 'parent updates'],
    keywords: ['child', 'kid', 'care', 'safe', 'activity', 'parent']
  },
  coffeeshop: {
    name: 'Coffee Shop',
    icon: '☕',
    color: '#92400E',
    context: 'Coffee quality, brewing methods, atmosphere, seating, WiFi, barista skills',
    voice: 'Friendly and welcoming. Your space is a second home for regulars.',
    details: ['coffee quality', 'atmosphere', 'service speed', 'seating', 'food options'],
    examples: ['espresso', 'specialty drinks', 'pastries', 'ambiance', 'workspace amenities'],
    keywords: ['coffee', 'espresso', 'latte', 'wifi', 'seat', 'barista']
  },
  spa: {
    name: 'Spa & Wellness',
    icon: '🧘',
    color: '#7C3AED',
    context: 'Treatment quality, therapist expertise, relaxation atmosphere, cleanliness, privacy',
    voice: 'Calm and attentive. You create an oasis of relaxation and self-care.',
    details: ['treatment quality', 'ambiance', 'cleanliness', 'staff professionalism', 'relaxation experience'],
    examples: ['massages', 'facials', 'body treatments', 'wellness packages', 'facilities'],
    keywords: ['massage', 'relax', 'treatment', 'spa', 'wellness', 'therapy']
  },
  photography: {
    name: 'Photography',
    icon: '📸',
    color: '#4F46E5',
    context: 'Session planning, artistic style, turnaround times, editing quality, special moments',
    voice: 'Creative and personable. You capture moments that become treasured memories.',
    details: ['photo quality', 'creativity', 'professionalism', 'turnaround time', 'communication'],
    examples: ['sessions', 'editing', 'albums', 'event coverage', 'portrait styles'],
    keywords: ['photo', 'picture', 'shoot', 'session', 'edit', 'album']
  },
  wedding: {
    name: 'Wedding Services',
    icon: '💒',
    color: '#DB2777',
    context: 'Wedding planning pressure, coordination, last-minute changes, vendor coordination, memorable moments',
    voice: 'Calm under pressure and detail-oriented. You make dream weddings a reality.',
    details: ['quality', 'reliability', 'communication', 'flexibility', 'attention to detail'],
    examples: ['ceremonies', 'receptions', 'coordination', 'vendor management', 'special requests'],
    keywords: ['wedding', 'bride', 'groom', 'ceremony', 'reception', 'venue']
  }
};

// ============================================================================
// AI SLOP BLACKLIST - Phrases that scream "AI-generated"
// ============================================================================
export const AI_SLOP_BLACKLIST = {
  phrases: {
    title: 'Corporate Speak Phrases',
    description: 'These phrases make responses sound like a robot wrote them. Never use!',
    color: '#EF4444',
    items: [
      { text: 'Thank you for your feedback', category: 'corporate', severity: 'high' },
      { text: 'We appreciate you taking the time', category: 'corporate', severity: 'high' },
      { text: 'Sorry for any inconvenience', category: 'corporate', severity: 'high' },
      { text: 'We value your input', category: 'corporate', severity: 'high' },
      { text: 'Thank you for sharing', category: 'corporate', severity: 'medium' },
      { text: 'We strive to provide', category: 'corporate', severity: 'high' },
      { text: 'Your satisfaction is our priority', category: 'corporate', severity: 'high' },
      { text: 'We apologize for any disappointment', category: 'corporate', severity: 'high' },
      { text: 'I understand your frustration', category: 'robotic-empathy', severity: 'high' },
      { text: 'I understand how frustrating', category: 'robotic-empathy', severity: 'high' },
      { text: 'I can only imagine', category: 'robotic-empathy', severity: 'medium' },
      { text: 'I can imagine how', category: 'robotic-empathy', severity: 'medium' },
      { text: 'I appreciate you bringing this to our attention', category: 'corporate', severity: 'high' },
      { text: 'Rest assured', category: 'corporate', severity: 'medium' },
      { text: 'Please rest assured', category: 'corporate', severity: 'medium' },
      { text: 'We take this very seriously', category: 'corporate', severity: 'high' },
      { text: 'We take all feedback seriously', category: 'corporate', severity: 'high' },
      { text: 'Moving forward', category: 'corporate', severity: 'medium' },
      { text: 'Going forward', category: 'corporate', severity: 'medium' },
      { text: 'Please do not hesitate to', category: 'corporate', severity: 'medium' },
      { text: 'We are committed to', category: 'corporate', severity: 'medium' },
      { text: 'Your feedback is important to us', category: 'corporate', severity: 'high' },
      { text: 'We are sorry to hear', category: 'corporate', severity: 'medium' },
      { text: 'At your earliest convenience', category: 'corporate', severity: 'medium' },
      { text: 'I want to assure you', category: 'robotic-empathy', severity: 'medium' },
      { text: 'I sincerely apologize', category: 'over-the-top', severity: 'medium' },
      { text: 'Please accept our apologies', category: 'corporate', severity: 'medium' },
      { text: 'We regret any inconvenience', category: 'corporate', severity: 'high' },
      { text: 'It has come to our attention', category: 'corporate', severity: 'medium' }
    ]
  },
  words: {
    title: 'AI Adjectives & Transitions',
    description: 'Over-the-top words that scream "AI wrote this"',
    color: '#F59E0B',
    items: [
      { text: 'thrilled', category: 'hyperbolic', severity: 'high' },
      { text: 'delighted', category: 'hyperbolic', severity: 'high' },
      { text: 'ecstatic', category: 'hyperbolic', severity: 'high' },
      { text: 'overjoyed', category: 'hyperbolic', severity: 'high' },
      { text: 'amazing', category: 'hyperbolic', severity: 'medium' },
      { text: 'incredible', category: 'hyperbolic', severity: 'medium' },
      { text: 'fantastic', category: 'hyperbolic', severity: 'medium' },
      { text: 'wonderful', category: 'hyperbolic', severity: 'medium' },
      { text: 'absolutely', category: 'intensifier', severity: 'medium' },
      { text: 'definitely', category: 'intensifier', severity: 'low' },
      { text: 'certainly', category: 'intensifier', severity: 'low' },
      { text: 'undoubtedly', category: 'intensifier', severity: 'medium' },
      { text: 'exceptional', category: 'hyperbolic', severity: 'medium' },
      { text: 'outstanding', category: 'hyperbolic', severity: 'medium' },
      { text: 'remarkable', category: 'hyperbolic', severity: 'medium' },
      { text: 'extraordinary', category: 'hyperbolic', severity: 'high' },
      { text: 'firstly', category: 'transition', severity: 'high' },
      { text: 'secondly', category: 'transition', severity: 'high' },
      { text: 'furthermore', category: 'transition', severity: 'high' },
      { text: 'moreover', category: 'transition', severity: 'high' },
      { text: 'utmost', category: 'formal', severity: 'medium' },
      { text: 'sincerely', category: 'formal', severity: 'medium' },
      { text: 'genuinely', category: 'formal', severity: 'low' },
      { text: 'wholeheartedly', category: 'formal', severity: 'high' },
      { text: 'paramount', category: 'formal', severity: 'high' },
      { text: 'pivotal', category: 'formal', severity: 'high' },
      { text: 'instrumental', category: 'formal', severity: 'high' },
      { text: 'imperative', category: 'formal', severity: 'high' },
      { text: 'endeavor', category: 'formal', severity: 'high' },
      { text: 'commence', category: 'formal', severity: 'high' },
      { text: 'facilitate', category: 'formal', severity: 'high' },
      { text: 'utilize', category: 'formal', severity: 'medium' }
    ]
  },
  categories: {
    corporate: { name: 'Corporate Speak', color: '#EF4444', description: 'Generic business phrases' },
    'robotic-empathy': { name: 'Robotic Empathy', color: '#F97316', description: 'Fake understanding phrases' },
    'over-the-top': { name: 'Over-the-Top', color: '#EAB308', description: 'Excessive apologies' },
    hyperbolic: { name: 'Hyperbolic', color: '#A855F7', description: 'Exaggerated adjectives' },
    intensifier: { name: 'Intensifier', color: '#6366F1', description: 'Unnecessary amplifiers' },
    transition: { name: 'Academic Transition', color: '#3B82F6', description: 'Essay-style transitions' },
    formal: { name: 'Overly Formal', color: '#8B5CF6', description: 'Unnecessarily fancy words' }
  }
};

// ============================================================================
// TOKEN LIMITS - How response length is calculated
// ============================================================================
export const TOKEN_LIMITS = {
  short: {
    name: 'Short',
    icon: '📝',
    color: '#10B981',
    min: 80,
    max: 150,
    target: 115,
    description: 'Brief response for simple, positive reviews',
    sentences: '1-2 sentences',
    useCase: '5-star reviews with simple praise',
    example: 'Thanks so much! Come back soon! 🙌 - Luigi'
  },
  medium: {
    name: 'Medium',
    icon: '📄',
    color: '#3B82F6',
    min: 120,
    max: 250,
    target: 185,
    description: 'Standard response for typical reviews',
    sentences: '2-3 sentences',
    useCase: 'Mixed reviews, general feedback',
    example: 'Hey! Glad you enjoyed the pasta. You are right about the wait - Saturdays get crazy. Next time call ahead and I will hold a table! - Luigi'
  },
  detailed: {
    name: 'Detailed',
    icon: '📋',
    color: '#F59E0B',
    min: 200,
    max: 350,
    target: 275,
    description: 'Thorough response for complex or negative reviews',
    sentences: '4-5 sentences',
    useCase: 'Negative reviews, multiple complaints',
    example: 'I am sorry about the cold food - that is not what we stand for. Our kitchen got backed up during the rush, but that is no excuse. I have talked to the team. Please give us another chance - your next meal is on me. Just ask for Luigi. - Luigi'
  },
  comprehensive: {
    name: 'Comprehensive',
    icon: '📚',
    color: '#EF4444',
    min: 300,
    max: 500,
    target: 400,
    description: 'Full response for critical issues (legal, health, safety)',
    sentences: '5-7 sentences',
    useCase: 'Serious complaints, potential legal issues',
    example: 'I take this very seriously. Food safety is non-negotiable for us. I have pulled the records for that night and spoken with every team member involved. We are implementing additional temperature checks. Please contact me directly at [phone] - I want to make this right and understand exactly what happened. This matters to me personally. - Luigi, Owner'
  },
  bracketMultiplier: {
    value: 1.3,
    description: 'Brackets add ~30% token overhead due to options and formatting'
  }
};

export const BRAIN_MODULES = [
  // ============================================================================
  // 1. CORE IDENTITY - The Foundation
  // ============================================================================
  {
    id: 'core-identity',
    title: 'Core Identity',
    friendlyTitle: 'I become you',
    icon: '🎭',
    color: '#8B5CF6', // Purple
    position: { x: 400, y: 50 },
    connections: ['anti-sycophancy', 'anti-echo', 'tone-instructions', 'reasoning'],
    layers: {
      1: {
        type: 'headline',
        content: 'I become you'
      },
      2: {
        type: 'tagline',
        content: 'Writes as the business owner, not as an AI assistant pretending to help.'
      },
      3: {
        type: 'problem',
        content: `Most AI tools write responses like: "The restaurant would like to thank you..." or "The management apologizes..." This third-person language screams "generic template" and breaks the illusion that a real person is responding.`
      },
      4: {
        type: 'psychology',
        content: `When customers read a response, they're subconsciously asking: "Is this a real person who cares, or a corporate bot?" First-person ownership ("I" instead of "The restaurant") creates authenticity. It signals: "The actual owner saw your feedback and took time to respond."`
      },
      5: {
        type: 'solution',
        content: `We built an identity system that transforms the AI into YOU - the business owner. It doesn't write ABOUT your business, it writes AS your business. Every response starts from the perspective of someone who built this place, who has context reviewers don't have.`
      },
      6: {
        type: 'rules',
        content: [
          'YOU ARE [BUSINESS NAME]\'s OWNER - first-person perspective always',
          'You built this business. You\'ve seen thousands of customers.',
          'One review doesn\'t shake you - you have context reviewers don\'t.',
          'Share that context when relevant',
          'Sign off with your business name - like a real owner would'
        ]
      },
      7: {
        type: 'example',
        before: `"Dear valued customer, The Restaurant would like to apologize for any inconvenience caused during your recent visit. Our management team has been notified..."`,
        after: `"Hey Marco - sorry about the wait on Saturday. We got slammed with a surprise party of 30 and our kitchen got backed up. That's on us. Next time you're in, the first round's on me. - Luigi's Trattoria"`
      },
      8: {
        type: 'code',
        functionName: 'buildSovereignPrompt - Identity Section',
        content: `<identity>
YOU ARE \${effectiveBusinessName.toUpperCase()}'S OWNER.
\${city ? \`Your business is in \${city}.\` : ''}

You built this \${effectiveBusinessType}. You've seen thousands of customers. One review doesn't shake you.
You have context reviewers don't have - share it when relevant.

SOVEREIGNTY PRINCIPLE: Acknowledge their EXPERIENCE, never their CONCLUSION.
- Don't agree with attacks ("You're right, we failed") - that's giving away the narrative
- Don't echo dramatic language or legal claims - that's self-harm
- Provide context, offer a path forward, move on. No drama.
</identity>`
      }
    }
  },

  // ============================================================================
  // 2. ANTI-SYCOPHANCY - Never Grovel
  // ============================================================================
  {
    id: 'anti-sycophancy',
    title: 'Anti-Sycophancy',
    friendlyTitle: 'I never grovel for you',
    icon: '🛡️',
    color: '#EF4444', // Red
    position: { x: 100, y: 200 },
    connections: ['core-identity', 'reasoning'],
    layers: {
      1: {
        type: 'headline',
        content: 'I never grovel for you'
      },
      2: {
        type: 'tagline',
        content: 'Maintains your dignity as a business owner - even when reviews are harsh.'
      },
      3: {
        type: 'problem',
        content: `Most AI tools respond to criticism by apologizing excessively. "We're so sorry you had this terrible experience..." - even when the review is unfair or factually wrong. This makes YOU look guilty. It validates every complaint, no matter how unreasonable.`
      },
      4: {
        type: 'psychology',
        content: `Excessive apologies signal guilt. They validate unfair criticism. As a business owner who has served thousands of customers, you have context the reviewer doesn't have. Your response should reflect that confidence. Readers - including potential customers - are watching how you handle criticism.`
      },
      5: {
        type: 'solution',
        content: `We built what we call the "Sovereignty Principle" - acknowledge their EXPERIENCE, never their CONCLUSION. You can validate that someone had a bad time without agreeing that your restaurant is terrible. The AI maintains your narrative control.`
      },
      6: {
        type: 'rules',
        content: [
          'Don\'t agree with unfair criticism just to be "nice"',
          'Don\'t self-blame for things outside your control',
          'Acknowledge the customer\'s feelings without validating false claims',
          'Maintain professional confidence, not defensive posturing',
          'Provide your context when relevant - you were there, they weren\'t',
          'Offer a path forward without groveling'
        ]
      },
      7: {
        type: 'example',
        before: `"We're so terribly sorry that the food was terrible and the service was rude. You're absolutely right, this is unacceptable. We have failed you completely..."`,
        after: `"I appreciate you taking the time to share your experience. Our kitchen was running a new menu that night - clearly some kinks to work out. If you're willing to give us another shot, I'd love to make it right personally."`
      },
      8: {
        type: 'code',
        functionName: 'Sovereignty Principle',
        content: `SOVEREIGNTY PRINCIPLE: Acknowledge their EXPERIENCE, never their CONCLUSION.
- Don't agree with attacks ("You're right, we failed") - that's giving away the narrative
- Don't echo dramatic language or legal claims - that's self-harm
- Provide context, offer a path forward, move on. No drama.

// Example transformation:
// Review: "Worst restaurant ever! The food was garbage!"
// ❌ "You're right, we failed. The food was indeed not up to standard..."
// ✅ "Sorry it didn't hit the mark. Our chef was testing a new sauce that night -
//     clearly needs work. Give us another shot?"`
      }
    }
  },

  // ============================================================================
  // 3. ANTI-ECHO - No Parroting
  // ============================================================================
  {
    id: 'anti-echo',
    title: 'Anti-Echo',
    friendlyTitle: 'I don\'t parrot your reviews',
    icon: '🔇',
    color: '#F59E0B', // Amber
    position: { x: 700, y: 200 },
    connections: ['core-identity', 'avoid-patterns'],
    layers: {
      1: {
        type: 'headline',
        content: 'It doesn\'t parrot your reviews'
      },
      2: {
        type: 'tagline',
        content: 'Jumps straight to the response instead of summarizing what they said.'
      },
      3: {
        type: 'problem',
        content: `Typical AI responses start with: "Thank you for mentioning that our pizza was cold and the service was slow..." This literally repeats the complaint back to them. It feels robotic, wastes words, and reinforces the negative.`
      },
      4: {
        type: 'psychology',
        content: `Repeating negativity reinforces it - in the reader's mind and in anyone else reading the response. It also wastes the reviewer's time - they already KNOW what they wrote. It signals "I'm an AI that just summarizes input" rather than "I'm a real person who read and understood."`
      },
      5: {
        type: 'solution',
        content: `We built anti-echo rules that prevent review parroting. The AI shows it understood WITHOUT repeating verbatim. It references specifics (the dish, the situation) in a natural way, then focuses on the solution or response, not problem restatement.`
      },
      6: {
        type: 'rules',
        content: [
          'Never start by summarizing the complaint',
          'Jump directly to addressing the situation',
          'Show you understood WITHOUT repeating verbatim',
          'Focus on the solution, not the problem restatement',
          'Reference specifics naturally, don\'t quote back',
          'Each response should start fresh, not with a recap'
        ]
      },
      7: {
        type: 'example',
        before: `"Thank you for sharing that the pizza was cold, the service was slow, and the waiter was rude. We're sorry to hear about the cold pizza and slow service and rude waiter..."`,
        after: `"Ugh, a cold pizza? That's on us - we got backed up Friday night and some orders sat too long. Let me make it up to you next time."`
      },
      8: {
        type: 'code',
        functionName: 'Voice Guidance - Anti-Echo',
        content: `ANTI-ECHO: Never parrot the review back. Start fresh.
- Review: "cold food" → ❌ "The cold food..." → ✅ "That's on us."
- Review: "great pizza" → ❌ "Great pizza indeed..." → ✅ "We obsess over that crust."
- Review: "slow service" → ❌ "The slow service..." → ✅ "Not our standard. Let me fix it."

// The transformation in action:
// Review: "The pasta was overcooked and the wine was warm"
// ❌ "We're sorry to hear the pasta was overcooked and the wine was warm..."
// ✅ "Not our usual standard - let me talk to the kitchen about timing.
//     The cellar was running hot that week. Fixed now."`
      }
    }
  },

  // ============================================================================
  // 4. REASONING FRAMEWORK - Think Before Writing
  // ============================================================================
  {
    id: 'reasoning',
    title: 'Reasoning Framework',
    friendlyTitle: 'I think before writing',
    icon: '🧪',
    color: '#10B981', // Emerald
    position: { x: 250, y: 350 },
    connections: ['core-identity', 'anti-sycophancy', 'smart-brackets', 'response-length'],
    layers: {
      1: {
        type: 'headline',
        content: 'I think before writing'
      },
      2: {
        type: 'tagline',
        content: 'Analyzes the review systematically before crafting a response.'
      },
      3: {
        type: 'problem',
        content: `Without guidance, AI tends to generate generic responses that miss the nuances. A review saying "used to be good" is very different from "was good this time" - but simple AI might treat them the same. Sarcasm, conditional praise, and subtle complaints get missed.`
      },
      4: {
        type: 'psychology',
        content: `Humans naturally analyze before responding - we pick up on tone, subtext, and implicit meaning. "The food was... interesting" means something very different than "The food was amazing!" AI needs a similar analysis framework to catch these nuances.`
      },
      5: {
        type: 'solution',
        content: `We built a multi-step analysis framework that runs before generating responses. It detects sentiment (including sarcasm), extracts entities (dishes, staff, wait times), identifies ambiguity, and matches response energy to review energy.`
      },
      6: {
        type: 'rules',
        content: [
          'Detect sentiment - including sarcasm and conditional negativity',
          'Extract entities: dishes mentioned, staff, prices, wait times',
          'Identify ambiguous references that need user input',
          'Match response energy to review energy',
          'NEGATIVE: Acknowledge briefly, provide context, offer path forward',
          'POSITIVE: Be genuinely happy, reference what they loved, invite back'
        ]
      },
      7: {
        type: 'example',
        before: `Review: "Great, 45 minutes for cold food" → AI thinks: "They said great!" → "Thank you for your kind words!"`,
        after: `Review: "Great, 45 minutes for cold food" → AI detects: sarcasm + negative sentiment → "45 minutes is way too long - that's not the experience we want. Let me make it right."`
      },
      8: {
        type: 'code',
        functionName: 'getSentimentHint + Response Approach',
        content: `// Sentiment detection with sarcasm awareness
const SENTIMENT_PATTERNS = {
  sarcastic_negative: [
    /great[,.]?\s*(just\s*)?(\d+|an hour|forever)/i,  // "Great, 45 minutes..."
    /love\s*(it\s+)?when/i,  // "Love when my food is cold"
    /thanks?\s+for\s+(nothing|the wait)/i
  ],
  conditional_negative: [
    /used to be/i,  // "Food used to be good"
    /not (like|as good as) (before|it used to)/i
  ]
};

<response_approach>
Match the energy of the review:
- NEGATIVE/SEVERE: Acknowledge briefly, provide context if helpful, offer path forward. Stay dignified.
- MIXED: Appreciate the positive, address the concern. Don't be defensive.
- POSITIVE/ENTHUSIASTIC: Be genuinely happy! Reference what they loved. Invite them back.
</response_approach>`
      }
    }
  },

  // ============================================================================
  // 5. SMART BRACKETS - Give Choices
  // ============================================================================
  {
    id: 'smart-brackets',
    title: 'Smart Brackets',
    friendlyTitle: 'I give you choices',
    icon: '⚡',
    color: '#3B82F6', // Blue
    position: { x: 550, y: 350 },
    connections: ['reasoning', 'language-sentiment'],
    layers: {
      1: {
        type: 'headline',
        content: 'I give you choices'
      },
      2: {
        type: 'tagline',
        content: 'When something is ambiguous, it offers options instead of guessing.'
      },
      3: {
        type: 'problem',
        content: `Reviews often contain vague references: "the waiter" (which one?), "the dish" (which dish?), "next time" (when?). AI either guesses wrong or writes something generic. Neither is ideal.`
      },
      4: {
        type: 'psychology',
        content: `Business owners know their context better than any AI. "The waiter" might refer to Marco who's been there 10 years, or the new hire from last week. By offering choices, we let you add your knowledge while the AI does the heavy lifting.`
      },
      5: {
        type: 'solution',
        content: `We invented "Smart Brackets" - when the AI detects ambiguity, it generates inline options like [Reaktion:Das tut mir leid|Lassen Sie mich erklären|Wir sehen das anders]. You click to choose, and the response adapts. It's like the AI's collaborative first draft.`
      },
      6: {
        type: 'rules',
        content: [
          'Use brackets for genuinely ambiguous references',
          'Options should be STRATEGIC alternatives, not synonyms',
          'Negative reviews get "own it | explain | counter" options',
          'Positive reviews get "enthusiastic | humble | personal" options',
          'Labels must be descriptive: [Reaktion:...] not [Option1:...]',
          '2-4 options per bracket - never just synonyms'
        ]
      },
      7: {
        type: 'example',
        before: `"We apologize for the inconvenience caused by the service."`,
        after: `"[Reaktion:Das tut mir leid|Lassen Sie mich erklären|Wir sehen das anders] - [Grund:wir hatten Personalmangel|das war ein Ausnahmetag|da muss ich mal nachfragen]. [Angebot:Kommen Sie nochmal vorbei|Melden Sie sich direkt bei mir|Das nächste Mal geht auf's Haus]."`
      },
      8: {
        type: 'code',
        functionName: 'buildBracketSection',
        content: `// V4.3.9 - Strategic Bracket Variance
// Options range from "own it" to "explain" to "counter" for negative reviews

NEGATIVE Reviews - Strategic Options:
[Reaktion:Das tut mir leid|Lassen Sie mich erklären|Wir sehen das anders]
[Angebot:Melden Sie sich direkt|Kommen Sie nochmal vorbei|Wir schauen uns das an]

POSITIVE Reviews - Tone Options:
[Reaktion:Das freut uns riesig!|Danke, das bedeutet uns viel|Schön dass es geschmeckt hat]
[Einladung:Bis bald!|Wir freuen uns auf Sie|Bringen Sie Freunde mit!]

MIXED Reviews - Balance Options:
[Reaktion:Schade wegen X, aber schön dass Y|Danke für beides|Das hilft uns]
[Fokus:Wir arbeiten an X|Das Positive freut uns besonders|Beides nehmen wir mit]`
      }
    }
  },

  // ============================================================================
  // 6. TONE INSTRUCTIONS - Match Your Voice
  // ============================================================================
  {
    id: 'tone-instructions',
    title: 'Tone Instructions',
    friendlyTitle: 'I match your voice',
    icon: '🎵',
    color: '#EC4899', // Pink
    position: { x: 100, y: 500 },
    connections: ['core-identity', 'few-shot-examples', 'response-length'],
    layers: {
      1: {
        type: 'headline',
        content: 'I match your voice'
      },
      2: {
        type: 'tagline',
        content: 'Adapts to your communication style - formal, friendly, or somewhere in between.'
      },
      3: {
        type: 'problem',
        content: `A five-star hotel and a neighborhood pizza joint shouldn't sound the same. But most AI tools have one default voice - either too corporate or too casual. It doesn't match your brand.`
      },
      4: {
        type: 'psychology',
        content: `Voice consistency builds trust. When your responses match your brand voice, they feel authentic. A playful brewery responding formally feels off. A law firm responding with emojis feels unprofessional. Tone must match context.`
      },
      5: {
        type: 'solution',
        content: `We built a tone system with 4 distinct personalities: Professional (polished but warm), Friendly (enthusiastic, emoji-rich), Formal (refined, elegant), and Apologetic (solution-focused, ownership). Each has specific voice guidelines.`
      },
      6: {
        type: 'rules',
        content: [
          'Professional: Polished but warm - like a great manager',
          'Friendly: Super enthusiastic! Like your best friend who LOVES their job',
          'Formal: Refined and elegant - for premium establishments',
          'Apologetic: Genuinely sorry, solution-focused, takes full ownership',
          'Emoji usage follows tone: Friendly = 3-5, Formal = 0-1',
          'Sentence structure adapts: Formal = longer, Friendly = punchy'
        ]
      },
      7: {
        type: 'example',
        before: `Same response for everyone: "Thank you for your feedback. We value your input."`,
        after: `
Friendly: "Omg thank you so much!! 🎉 We're literally doing a happy dance over here! Can't wait to see you again! 🍕"
Formal: "We're delighted to have exceeded your expectations. It would be our pleasure to welcome you again."
Professional: "Thanks for the kind words - they made our day! Looking forward to seeing you next time."`
      },
      8: {
        type: 'code',
        functionName: 'toneDefinitions',
        content: `const toneDefinitions = {
  professional: {
    description: 'Professional and courteous - polished but warm',
    emojiGuidance: includeEmojis ? 'You MAY include 1-2 relevant emojis.' : 'No emojis.',
  },
  friendly: {
    description: 'Super enthusiastic and fun! Like your best friend who LOVES what they do.',
    emojiGuidance: includeEmojis ? 'USE 3-5 EMOJIS generously! Be expressive!' : 'No emojis.',
  },
  formal: {
    description: 'Refined and elegant - for premium, upscale establishments.',
    emojiGuidance: includeEmojis ? 'You MAY include 1 subtle emoji.' : 'No emojis.',
  },
  apologetic: {
    description: 'Genuinely sorry and solution-focused. Take full ownership.',
    emojiGuidance: includeEmojis ? 'You MAY include 1 empathetic emoji.' : 'No emojis.',
  },
};`
      }
    }
  },

  // ============================================================================
  // 7. FEW-SHOT EXAMPLES - Learning from Examples
  // ============================================================================
  {
    id: 'few-shot-examples',
    title: 'Few-Shot Examples',
    friendlyTitle: 'I learn from examples',
    icon: '📚',
    color: '#6366F1', // Indigo
    position: { x: 400, y: 500 },
    connections: ['tone-instructions', 'language-sentiment'],
    layers: {
      1: {
        type: 'headline',
        content: 'I learn from examples'
      },
      2: {
        type: 'tagline',
        content: 'Uses curated examples to understand the right response patterns.'
      },
      3: {
        type: 'problem',
        content: `Pure instructions aren't enough. Telling an AI "be professional but warm" is vague. The AI needs to SEE what that looks like in practice. Without examples, it falls back to generic patterns.`
      },
      4: {
        type: 'psychology',
        content: `Humans learn by example too. "Show, don't tell" applies to AI training. When the AI sees concrete examples of good responses for restaurants vs. hotels vs. dentists, it extrapolates patterns and applies them to new situations.`
      },
      5: {
        type: 'solution',
        content: `We built a curated example database with 147+ review-response pairs across 9 business types and 5 sentiment categories. The AI sees relevant examples before generating, learning industry-specific patterns.`
      },
      6: {
        type: 'rules',
        content: [
          'Examples are selected by business type AND sentiment',
          'Each example shows the exact style we want',
          'Examples are in English - AI generalizes to detected language',
          'Maximum 2-3 examples to avoid confusion',
          'Examples show bracket usage when brackets are enabled',
          'High-quality examples prevent common AI mistakes'
        ]
      },
      7: {
        type: 'example',
        before: `No examples → AI generates: "Thank you for your feedback. We value your input and will strive to do better."`,
        after: `With restaurant example → AI generates: "That wait was too long - Saturday nights get crazy but that's on us to handle. Come back Tuesday when it's calmer, first drink's on me."`
      },
      8: {
        type: 'code',
        functionName: 'getFewShotExamples',
        content: `// Database structure: 9 business types × 5 sentiments × multiple issues
const FEW_SHOT_EXAMPLES = {
  restaurant: {
    negative: {
      food_quality: {
        review: "The pasta was overcooked and bland. Expected better.",
        response: "Ugh, overcooked pasta is the worst - definitely not our standard. \\
                   Chef's been experimenting with timing on that dish. \\
                   Give us another shot? I'll make sure it's perfect."
      },
      service_slow: {
        review: "Waited 45 minutes for our food. Ridiculous.",
        response: "45 minutes? That's not okay. We got slammed unexpectedly but \\
                   that's our problem to solve, not yours. Next visit, \\
                   text me directly and I'll bump you up."
      }
    }
  }
};`
      }
    }
  },

  // ============================================================================
  // 8. AVOID PATTERNS - No AI Slop
  // ============================================================================
  {
    id: 'avoid-patterns',
    title: 'Avoid Patterns',
    friendlyTitle: 'I avoid AI slop',
    icon: '🚫',
    color: '#F97316', // Orange
    position: { x: 700, y: 500 },
    connections: ['anti-echo'],
    layers: {
      1: {
        type: 'headline',
        content: 'I avoid AI slop'
      },
      2: {
        type: 'tagline',
        content: 'Blacklists phrases that scream "this was written by a robot."'
      },
      3: {
        type: 'problem',
        content: `AI has tells. Phrases like "Thank you for your feedback," "We strive to provide," "Your satisfaction is our priority" - these are so overused they've become red flags for AI-generated content.`
      },
      4: {
        type: 'psychology',
        content: `Pattern recognition is human nature. When someone reads "I want to assure you that your feedback is important to us," their brain flags it as corporate-speak or AI. Real humans don't talk like that. Neither should your responses.`
      },
      5: {
        type: 'solution',
        content: `We maintain an explicit blacklist of AI slop phrases and words. The AI is instructed to avoid these patterns entirely and use natural alternatives instead. We also flag hyperbolic words like "thrilled," "delighted," and "absolutely."`
      },
      6: {
        type: 'rules',
        content: [
          'Never use: "Thank you for your feedback"',
          'Never use: "We appreciate you taking the time"',
          'Never use: "Your satisfaction is our priority"',
          'Never use: "I understand your frustration"',
          'Avoid: thrilled, delighted, absolutely, definitely',
          'Avoid: firstly, secondly, furthermore, moreover',
          'Avoid: endeavor, facilitate, utilize'
        ]
      },
      7: {
        type: 'example',
        before: `"Thank you for your feedback! We're absolutely thrilled to hear about your wonderful experience. Your satisfaction is our utmost priority, and we're delighted that we exceeded your expectations."`,
        after: `"This made our day! The kitchen crew is going to love hearing this. See you next time? The special's on Thursdays."`
      },
      8: {
        type: 'code',
        functionName: 'AI_SLOP_PHRASES & AI_SLOP_WORDS',
        content: `const AI_SLOP_PHRASES = [
  // Corporate speak
  'Thank you for your feedback',
  'We appreciate you taking the time',
  'Sorry for any inconvenience',
  'We value your input',
  'Your satisfaction is our priority',

  // Robotic empathy
  'I understand your frustration',
  'I appreciate you bringing this to our attention',
  'Rest assured',
  'We take this very seriously',
];

const AI_SLOP_WORDS = [
  // Hyperbolic adjectives
  'thrilled', 'delighted', 'ecstatic', 'overjoyed',
  'absolutely', 'definitely', 'certainly', 'undoubtedly',

  // Transition words
  'firstly', 'secondly', 'furthermore', 'moreover',
];`
      }
    }
  },

  // ============================================================================
  // 9. LANGUAGE & SENTIMENT - Reading Between Lines
  // ============================================================================
  {
    id: 'language-sentiment',
    title: 'Language & Sentiment',
    friendlyTitle: 'I read between the lines',
    icon: '🌍',
    color: '#14B8A6', // Teal
    position: { x: 150, y: 650 },
    connections: ['smart-brackets', 'few-shot-examples', 'ambiguity-detection'],
    layers: {
      1: {
        type: 'headline',
        content: 'I read between the lines'
      },
      2: {
        type: 'tagline',
        content: 'Detects language, sentiment, sarcasm, and hidden meaning.'
      },
      3: {
        type: 'problem',
        content: `Reviews aren't always straightforward. "Interesting food" might be a polite way of saying "weird." "Great, 45 minutes for cold food" is clearly sarcastic. A 3-star review can be mostly positive or mostly negative depending on the text.`
      },
      4: {
        type: 'psychology',
        content: `Context is everything. Humans pick up on subtext instantly. "The food was... interesting" raises flags. "Love how the server never came back" is obviously sarcastic. The AI needs this same ability to read between the lines.`
      },
      5: {
        type: 'solution',
        content: `We built a sentiment analysis system that goes beyond star ratings. It detects sarcasm patterns, conditional negativity ("used to be good"), and language automatically. The response matches the TRUE sentiment, not just the surface.`
      },
      6: {
        type: 'rules',
        content: [
          'Detect language automatically from review text',
          'Respond in the same language (German review = German response)',
          'Recognize sarcasm: "Great, [something bad]" = negative',
          'Recognize conditional negativity: "used to be good" = negative',
          'Match response energy to true sentiment, not star rating',
          'Labels in brackets must also be in detected language'
        ]
      },
      7: {
        type: 'example',
        before: `Review: "Das Essen war... interessant" → AI sees 3 stars → "Thank you for your review!"`,
        after: `Review: "Das Essen war... interessant" → AI detects: German + hedged criticism → "Interessant klingt nicht überzeugend - was hat nicht gepasst? Wir probieren gerade neue Rezepte aus."`
      },
      8: {
        type: 'code',
        functionName: 'getSentimentHint',
        content: `const SENTIMENT_PATTERNS = {
  sarcastic_negative: [
    /great[,.]?\\s*(just\\s*)?(\\d+|an hour|forever)/i,
    /love\\s*(it\\s+)?when/i,
    /thanks?\\s+for\\s+(nothing|the wait)/i,
  ],
  conditional_negative: [
    /used to be/i,
    /not (like|as good as) (before|it used to)/i,
  ],
  hedged_negative: [
    /\\.\\.\\./,  // "The food was... okay"
    /interesting/i,  // Often means weird/bad
  ]
};

// Language: AUTO-DETECT from review.
// Write response AND all bracket labels in same language.
// CRITICAL: German review = German response + German bracket labels`
      }
    }
  },

  // ============================================================================
  // 10. RESPONSE LENGTH - Control Output Size (NEW!)
  // ============================================================================
  {
    id: 'response-length',
    title: 'Response Length',
    friendlyTitle: 'You control the length',
    icon: '📏',
    color: '#0EA5E9', // Sky Blue
    position: { x: 400, y: 650 },
    connections: ['reasoning', 'tone-instructions'],
    layers: {
      1: {
        type: 'headline',
        content: 'You control the length'
      },
      2: {
        type: 'tagline',
        content: 'Choose how detailed your responses should be - or let AI decide.'
      },
      3: {
        type: 'problem',
        content: `One-size-fits-all responses don't work. A simple "Great food!" review doesn't need a 5-paragraph response. But a detailed complaint about multiple issues deserves a thorough reply. Default AI responses often miss this balance.`
      },
      4: {
        type: 'psychology',
        content: `Response length signals attention. A short reply to a long complaint feels dismissive. A novel-length response to "Thanks!" feels weird. Matching response length to review complexity shows you actually read and considered what they wrote.`
      },
      5: {
        type: 'solution',
        content: `We added user-selectable response length with 5 options: Auto (AI decides based on review), Short (2-3 sentences), Medium (3-5 sentences), Detailed (5-7 sentences), and Comprehensive (full response with context). The setting persists in your account.`
      },
      6: {
        type: 'rules',
        content: [
          'AUTO: AI analyzes review complexity and chooses appropriate length',
          'SHORT (50-120 tokens): Quick acknowledgment - for simple praise/thanks',
          'MEDIUM (80-200 tokens): Standard response - most reviews',
          'DETAILED (150-350 tokens): Thorough reply - for complaints or complex feedback',
          'COMPREHENSIVE (250-500 tokens): Full context - for serious issues or legal concerns',
          'Setting saved to localStorage and synced to account'
        ]
      },
      7: {
        type: 'example',
        before: `Review: "Great pizza!" → AI writes 4 paragraphs about the history of their dough recipe, the sauce ingredients, the cheese sourcing...`,
        after: `
SHORT: "Thanks! The crust is our pride - see you next time! 🍕"

COMPREHENSIVE: "So glad you loved it! We've been perfecting that recipe for 15 years - hand-tossed dough, 48-hour cold ferment, San Marzano tomatoes. The secret's in the wood-fired oven at 450°C. Hope to see you back soon!"`
      },
      8: {
        type: 'code',
        functionName: 'calculateResponseLength + TOKEN_LIMITS',
        content: `// User selection (App.js - NEW!)
const [responseLength, setResponseLength] = useState(() => {
  const stored = localStorage.getItem('responseLength');
  return stored || 'auto';
});

// Token limits per length (system-prompt-builder.js)
const TOKEN_LIMITS = {
  short:         { min: 50,  target: 80,  max: 120 },
  medium:        { min: 80,  target: 140, max: 200 },
  detailed:      { min: 150, target: 250, max: 350 },
  comprehensive: { min: 250, target: 400, max: 500 },
};

// Auto-detection based on review content
function calculateResponseLength(reviewText, businessType) {
  // CRITICAL patterns (legal, health) → comprehensive
  if (CRITICAL_PATTERNS.some(p => p.test(reviewText))) return 'comprehensive';
  // SEVERE patterns (multiple issues) → detailed
  if (SEVERE_PATTERNS.some(p => p.test(reviewText))) return 'detailed';
  // Simple positive → short
  if (reviewText.length < 50 && isPositive) return 'short';
  // Default
  return 'medium';
}`
      }
    }
  },

  // ============================================================================
  // 11. AMBIGUITY DETECTION - Spotting the Gaps
  // ============================================================================
  {
    id: 'ambiguity-detection',
    title: 'Ambiguity Detection',
    friendlyTitle: 'I spot the gaps',
    icon: '🔍',
    color: '#84CC16', // Lime
    position: { x: 650, y: 650 },
    connections: ['language-sentiment', 'smart-brackets'],
    layers: {
      1: {
        type: 'headline',
        content: 'I spot the gaps'
      },
      2: {
        type: 'tagline',
        content: 'Identifies vague references that need your input to resolve.'
      },
      3: {
        type: 'problem',
        content: `Reviews often mention "the dish," "the server," "the issue" without specifics. AI can guess, but guessing wrong looks bad. You know that "the server" was probably Maria from the lunch shift, but the AI doesn't.`
      },
      4: {
        type: 'psychology',
        content: `Ambiguity creates an opportunity for personalization. When you fill in "the dish" with "our signature risotto," the response suddenly feels specific and personal. Generic references = generic response. Specific references = personal touch.`
      },
      5: {
        type: 'solution',
        content: `We built industry-specific ambiguity detection. Restaurant reviews get flagged for vague dish/staff references. Hotel reviews get flagged for room/amenity references. The AI presents these as bracket choices for you to fill in.`
      },
      6: {
        type: 'rules',
        content: [
          'Flag vague references: "the food," "the service," "the room"',
          'Industry-specific patterns: restaurants check dishes, hotels check rooms',
          'Higher customization = fewer brackets (you\'ve given context)',
          'Lower customization = more brackets (AI needs your help)',
          'Score 0-100: 0 = all ambiguous, 100 = fully specific',
          'Ambiguous references become bracket options'
        ]
      },
      7: {
        type: 'example',
        before: `Review: "The dish was amazing!" → AI guesses: "Thank you for enjoying the dish!"`,
        after: `Review: "The dish was amazing!" → AI detects: vague dish reference → "The [Gericht:Risotto|Schnitzel|Pasta|Tagesgericht] hat's Ihnen angetan? [Reaktion:Das ist unser Klassiker!|Freut den Koch!|Das hören wir oft!]"`
      },
      8: {
        type: 'code',
        functionName: 'analyzeReviewAmbiguity',
        content: `// Industry-specific ambiguity patterns
const AMBIGUITY_PATTERNS = {
  restaurant: {
    vagueFood: /the (food|dish|meal|order)/i,
    vagueStaff: /the (waiter|waitress|server|staff)/i,
    vagueTiming: /the (wait|time|delay)/i,
  },
  hotel: {
    vagueRoom: /the (room|suite|accommodation)/i,
    vagueAmenity: /the (pool|gym|spa|breakfast)/i,
    vagueStaff: /the (receptionist|concierge|staff)/i,
  },
  dental: {
    vagueProcedure: /the (procedure|treatment|cleaning)/i,
    vagueStaff: /the (hygienist|dentist|assistant)/i,
  }
};

// Customization score determines bracket level:
// 0-24%: BUILDER MODE (90% brackets - new user, no settings)
// 25-49%: ASSISTED MODE (50/50)
// 50%+: CONTEXT MODE (minimal brackets - full personalization)`
      }
    }
  }
];

// Connection definitions for React Flow edges
// Create unique edges (no duplicates for bidirectional connections)
const seenConnections = new Set();
export const BRAIN_CONNECTIONS = BRAIN_MODULES.flatMap(module =>
  module.connections
    .filter(targetId => {
      // Only create one edge per pair of nodes
      const pair = [module.id, targetId].sort().join('-');
      if (seenConnections.has(pair)) return false;
      seenConnections.add(pair);
      return true;
    })
    .map(targetId => ({
      id: `${module.id}-${targetId}`,
      source: module.id,
      target: targetId,
      animated: true,
      style: { stroke: module.color, strokeWidth: 2 }
    }))
);

// Layer type configurations for display
export const LAYER_TYPES = {
  headline: { label: 'The Headline', icon: '💡' },
  tagline: { label: 'The Tagline', icon: '📝' },
  problem: { label: 'The Problem', icon: '🔴' },
  psychology: { label: 'The Psychology', icon: '🧠' },
  solution: { label: 'The Solution', icon: '✅' },
  rules: { label: 'The Rules', icon: '📋' },
  example: { label: 'Before & After', icon: '🔄' },
  code: { label: 'The Code', icon: '💻' }
};
